import type { TradingPage } from './api'
import { useEffect, useState } from 'react'
import { fetchTradingPage, updateTradingPage } from './api'
import { linkifyText } from './linkify'
import { formatRelative, formatTsUtc } from './time'

interface Props {
  // Trading+ — same gate as TradingPanel.
  canEdit: boolean
}

// The global SPARKET page at /trading/sparket — one plain-text guide stored
// as the 'sparket' row in trading_pages. The content is a table migrated
// from a spreadsheet, so the view render is monospace + pre-wrap to keep the
// column alignment intact. Same edit lifecycle as a TradingPanel section.
export default function SparketPanel({ canEdit }: Props) {
  const [page, setPage] = useState<TradingPage | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchTradingPage('sparket')
      .then((data) => {
        if (!cancelled) setPage(data)
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
    return () => {
      cancelled = true
    }
  }, [])

  function startEdit() {
    setDraft(page?.content ?? '')
    setEditing(true)
    setMsg(null)
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      await updateTradingPage('sparket', draft)
      // Re-fetch the canonical row for the fresh updated_at / updated_by.
      const saved = await fetchTradingPage('sparket')
      setPage(saved)
      setEditing(false)
      setMsg({ kind: 'ok', text: 'Saved SPARKET guide.' })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <section className="ad-editor ad-editor--full">
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          <h2 className="b-h2">SPARKET</h2>
        </div>
      </header>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}

      {!page
        ? <p className="b-mono-dim b-tiny">Loading…</p>
        : (
            <div className="ad-editor-section">
              <div className="ad-section-head">
                <h3 className="b-mono">SPARKET GUIDE</h3>
                {canEdit && (
                  editing
                    ? (
                        <div className="ad-section-actions">
                          <button
                            type="button"
                            className="b-btn-ghost b-btn-tiny"
                            onClick={() => setEditing(false)}
                            disabled={busy}
                          >
                            CANCEL
                          </button>
                          <button
                            type="button"
                            className="b-btn b-btn-primary b-btn-tiny"
                            onClick={save}
                            disabled={busy}
                          >
                            {busy ? 'SAVING…' : 'SAVE'}
                          </button>
                        </div>
                      )
                    : (
                        <button type="button" className="b-btn b-btn-tiny" onClick={startEdit}>
                          EDIT
                        </button>
                      )
                )}
              </div>

              {editing
                ? (
                    <textarea
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      className="ad-trading-note-input"
                      rows={Math.max(12, draft.split('\n').length + 1)}
                      spellCheck={false}
                    />
                  )
                : page.content
                  ? <div className="ad-trading-note-body ad-trading-note-body--mono">{linkifyText(page.content)}</div>
                  : <p className="b-mono-dim b-tiny">— empty —</p>}

              {!editing && page.updated_at && (
                <p className="ad-trading-note-meta b-mono-dim b-tiny">
                  updated
                  {' '}
                  <span title={formatTsUtc(page.updated_at)}>{formatRelative(page.updated_at)}</span>
                  {page.updated_by && ` by ${page.updated_by}`}
                </p>
              )}
            </div>
          )}
    </section>
  )
}
